import {ApiResponse, ResponseStatus} from "../models/api-response.model";
import {EmployeeRole} from "../models/employee.model";
import {logoutUser} from "./user-account.service";

const AUTH_STORAGE_KEY = 'auth_user_data';

export interface GuestCompany {
    id: number,
    name: string,
    about: string
}

export interface LoginRequest {
    companyId: number,
    username: string,
    password: string
}

export interface LoginResponse {
    userId: number,
    firstName: string,
    middleName: string,
    lastName: string,
    email: string
    phone: string,
    address: string,
    sex?: string,
    companyId: number,
    companyName: string,
    aboutCompany: string,
    token: string
    refreshToken: string
    forcePasswordChange: boolean
    roles: EmployeeRole[]
}


export const loadGuestCompanies = async (): Promise<ApiResponse<GuestCompany[]>> => {
    try {
        const response = await fetch('/api/v1/common/guest-user-account/guest-companies');
        if (!response.ok) {
            console.error("Failed to load companies with status:", response.status);
            return {
                status: ResponseStatus.SERVER_ERROR,
                message: `Failed to load companies: ${response.statusText}`,
                body: []
            };
        }
        return await response.json();
    } catch (error) {
        console.error("Failed to load companies:", error);
        return {
            status: ResponseStatus.SERVER_ERROR,
            message: "Failed to load companies. Please check your connection and try again.",
            body: []
        };
    }
};

export const login = async (request: LoginRequest): Promise<ApiResponse<LoginResponse | null>> => {
    try {
        // Note: Login uses Basic auth, not Bearer token since user isn't authenticated yet
        const response = await fetch('/api/v1/common/guest-user-account/login', {
            method: 'POST',
            headers: {
                'Authorization': 'Basic ' + btoa(request.username + ':' + request.password + ':' + request.companyId),
                'Content-Type': 'application/json',
            }
        });

        if (!response.ok) {
            console.error("Login failed with status:", response.status);
            return {
                status: ResponseStatus.SERVER_ERROR,
                message: `Login failed with status ${response.status}: ${response.statusText}`,
                body: null
            };
        }

        return await response.json();
    } catch (error) {
        console.error("Login failed:", error);
        return {
            status: ResponseStatus.SERVER_ERROR,
            message: "Login failed. Please check your connection and try again.",
            body: null
        };
    }
};

// Helper function to check if user is authenticated
export const isAuthenticated = (): boolean => {
    const userData = localStorage.getItem(AUTH_STORAGE_KEY);
    return !!userData;
};

// Helper function to get current user data
export const readAuthData = (): LoginResponse | null => {
    const userData = localStorage.getItem(AUTH_STORAGE_KEY);
    if (userData) {
        try {
            return JSON.parse(userData);
        } catch (error) {
            console.error('Failed to parse user data:', error);
            clearAuthData();
            return null;
        }
    }
    return null;
};

export const storeAuthData = (loginResponse: LoginResponse): void => {
    try {
        localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(loginResponse));
    } catch (error) {
        console.error('Failed to store auth data:', error);
    }
};

export const clearAuthData = (): void => {
    try {
        localStorage.removeItem(AUTH_STORAGE_KEY);
    } catch (error) {
        console.error('Error clearing auth data:', error);
    }
};

export const refreshAuthToken = async (): Promise<ApiResponse<LoginResponse> | null> => {
    try {
        const user = readAuthData();
        if (!user || !user.refreshToken) {
            console.error("No user data or refresh token found");
            return null;
        }

        const response = await fetch('/api/v1/common/guest-user-account/refresh-token', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + user.refreshToken
            }
        });

        if (!response.ok) {
            console.error("Token refresh failed with status:", response.status);
            return null;
        }

        const data = await response.json();
        if (data.status !== ResponseStatus.SUCCESS || !data.body || !data.body.token) {
            console.error("Invalid token refresh response:", data);
            return null;
        }

        // Store the new token so subsequent requests use it
        storeAuthData(data.body);
        return data;
    } catch (error) {
        console.error("Token refresh error:", error);
        return null;
    }
};
